import { useEffect } from 'react';

interface OgMetaOptions {
  title?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
}

/**
 * Dynamically updates Open Graph and Twitter meta tags in document.head.
 * Restores the original values when the component unmounts.
 *
 * Note: this is effective for browser-based share previews and for future
 * static pre-rendering. Social crawlers that don't execute JS will still see
 * the static tags from index.html.
 */
export function useOgMeta({ title, description, imageUrl, url }: OgMetaOptions) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const selectors: Record<string, string | undefined> = {
      'meta[property="og:title"]': title,
      'meta[property="og:description"]': description,
      'meta[property="og:image"]': imageUrl,
      'meta[property="og:url"]': url,
      'meta[name="twitter:title"]': title,
      'meta[name="twitter:description"]': description,
      'meta[name="twitter:image"]': imageUrl,
      'meta[name="twitter:url"]': url,
    };

    // Snapshot originals and apply new values
    const originals: Record<string, string | null> = {};
    for (const [selector, value] of Object.entries(selectors)) {
      if (value === undefined) continue;
      const el = document.querySelector<HTMLMetaElement>(selector);
      if (el) {
        originals[selector] = el.getAttribute('content');
        el.setAttribute('content', value);
      }
    }

    // Also update <title>
    const origDocTitle = document.title;
    if (title) document.title = `${title} — MarketMind`;

    return () => {
      // Restore originals on unmount
      for (const [selector, original] of Object.entries(originals)) {
        const el = document.querySelector<HTMLMetaElement>(selector);
        if (el) {
          if (original === null) {
            el.removeAttribute('content');
          } else {
            el.setAttribute('content', original);
          }
        }
      }
      document.title = origDocTitle;
    };
  }, [title, description, imageUrl, url]);
}

/**
 * Build the absolute URL for a dynamic OG image from the /api/og endpoint.
 */
export function buildOgImageUrl(params: {
  title: string;
  probability?: number;
  summary?: string;
  platform?: string;
  recommendation?: string;
}): string {
  const base =
    typeof window !== 'undefined' ? window.location.origin : '';
  const qs = new URLSearchParams();
  qs.set('title', params.title);
  if (params.probability !== undefined) qs.set('probability', String(params.probability));
  if (params.summary) qs.set('summary', params.summary);
  if (params.platform) qs.set('platform', params.platform);
  if (params.recommendation) qs.set('recommendation', params.recommendation);
  return `${base}/api/og?${qs.toString()}`;
}
